import { getAuthToken } from '../utils/authUtils';
import requestDeduplicationService from './requestDeduplicationService';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Create a new notification for a user
 * @param {string} userId - The ID of the user to notify
 * @param {Object} notificationData - The notification data
 * @param {string} notificationData.type - Either 'action' or 'view'
 * @param {string} notificationData.title - The notification title
 * @param {string} notificationData.message - The notification message
 * @param {string} [notificationData.actionLink] - Optional link for action notifications
 * @param {Object} [notificationData.metadata] - Optional additional data
 * @returns {Promise<Object>} Created notification data
 */
export const createNotification = async (userId, notificationData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/notifications`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        ...notificationData
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Rate limiting for API calls
const lastFetchTime = new Map();
const MIN_FETCH_INTERVAL = 5000; // 5 seconds minimum between fetches

/**
 * Get user notifications with rate limiting
 * @param {string} userId - The user ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Notifications data
 */
export const getUserNotifications = async (userId, options = {}) => {
  const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/notifications/user/${userId}`, options);
  
  try {
    // Check for cached response first (fastest path)
    const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
    if (cachedResponse) {
      console.log('Using cached notifications response');
      return cachedResponse;
    }

    // Check if request is already in progress - if so, wait for it
    if (requestDeduplicationService.isRequestPending(cacheKey)) {
      console.log('Notifications request already in progress, waiting for completion...');
      return await requestDeduplicationService.trackRequest(cacheKey);
    }

    // Register this request as pending BEFORE making the fetch
    // This prevents race conditions where multiple requests slip through
    requestDeduplicationService.trackRequest(cacheKey);

    // Rate limiting check (keep existing rate limiting)
    const now = Date.now();
    const lastFetch = lastFetchTime.get(userId) || 0;
    const timeSinceLastFetch = now - lastFetch;
    
    if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
      console.log(`NotificationService: Rate limiting - skipping fetch for user ${userId}, last fetch was ${timeSinceLastFetch}ms ago`);
      const error = new Error('Rate limited - please wait before making another request');
      requestDeduplicationService.completeRequest(cacheKey, null, error);
      throw error;
    }
    
    // Update last fetch time
    lastFetchTime.set(userId, now);
    
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.unreadOnly) {
      queryParams.append('unreadOnly', 'true');
    }
    if (options.before) {
      queryParams.append('before', options.before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/notifications/user/${userId}?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const notifications = data.notifications || [];
    
    // Cache the response for 2 minutes (notifications change moderately)
    requestDeduplicationService.cacheResponse(cacheKey, notifications, 120000);
    
    // Complete the tracked request - this resolves all waiting promises
    requestDeduplicationService.completeRequest(cacheKey, notifications);

    return notifications;
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    
    // Complete the tracked request with error - this rejects all waiting promises
    requestDeduplicationService.completeRequest(cacheKey, null, error);
    
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} Update result
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Update result
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/notifications/user/${userId}/mark-all-read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteNotification = async (notificationId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};



/**
 * Subscribe to notifications for a user
 * @param {string} userId - The user ID
 * @param {Function} callback - Callback function to handle new notifications
 * @returns {Function} Cleanup function to unsubscribe
 */
export const subscribeToNotifications = (userId, callback) => {
  try {
    // For now, implement a simple polling mechanism
    // In the future, this could be WebSocket or server-sent events
    
    let isPolling = false;
    let lastNotificationCount = 0;
    
    // Set up polling interval
    const pollInterval = setInterval(async () => {
      // Prevent multiple simultaneous calls
      if (isPolling) {
        console.log('NotificationService: Skipping poll - previous request still in progress');
        return;
      }
      
      try {
        isPolling = true;
        const notifications = await getUserNotifications(userId, { unreadOnly: true });
        
        // Only call callback if notifications actually changed
        if (notifications && notifications.length !== lastNotificationCount) {
          lastNotificationCount = notifications.length;
          callback({ notifications });
        }
      } catch (error) {
        console.error('Error polling notifications:', error);
        // Don't update lastNotificationCount on error to allow retry
      } finally {
        isPolling = false;
      }
    }, 120000); // Poll every 120 seconds (2 minutes)
    
    // Return cleanup function
    return () => {
      clearInterval(pollInterval);
    };
  } catch (error) {
    console.error('Error setting up notification subscription:', error);
    // Return no-op cleanup function on error
    return () => {};
  }
};

/**
 * Mark a notification as complete
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} Update result
 */
export const markNotificationComplete = async (notificationId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/notifications/${notificationId}/complete`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking notification as complete:', error);
    throw error;
  }
};
